import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Button } from "../ui/button";
import { DateInput } from "../ui/dateInput";
import { format } from "date-fns";
import { cn } from "../../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { useToast } from "../../hooks/use-toast";
import api from "../../lib/api";
import Pagination from "../ui/pagination";
import { Loader } from "../layout/Loader";

interface MissingAttachmentsOrder {
  order_id: string;
  invoice_date: string;
  invoice_number: string;
  invoice_attachment: string;
  prescription_attachment: string;
  tracking_url: string;
  status: string;
}

const MissingAttachmentsTable = () => {
  const [allOrders, setAllOrders] = useState<MissingAttachmentsOrder[]>([]);
  const [displayedOrders, setDisplayedOrders] = useState<
    MissingAttachmentsOrder[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date | undefined>(
    new Date(new Date().setDate(new Date().getDate() - 7)),
  );
  const [endDate, setEndDate] = useState<Date | undefined>(new Date());
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [limit, setLimit] = useState<number>(50);
  const { toast } = useToast();

  // Update displayed orders when allOrders or pagination changes
  useEffect(() => {
    const startIndex = (currentPage - 1) * limit;
    const endIndex = startIndex + limit;
    setDisplayedOrders(allOrders.slice(startIndex, endIndex));
    setTotalCount(allOrders.length);
    setTotalPages(Math.ceil(allOrders.length / limit));
  }, [allOrders, currentPage, limit]);

  const fetchOrders = async () => {
    if (!startDate || !endDate) {
      toast({
        title: "Date range required",
        description: "Please select both start and end dates",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const response = await api.getOrdersWithMissingAttachments({
        start_date: format(startDate, "yyyy-MM-dd"),
        end_date: format(endDate, "yyyy-MM-dd"),
      });

      if (response.success) {
        setAllOrders(response.data);
        setCurrentPage(1); // Reset to first page when new data is loaded
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to fetch orders",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast({
        title: "Error",
        description: "Failed to fetch orders",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleLinkClick = (url: string) => {
    if (url !== "Missing") {
      window.open(url, "_blank");
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Orders with Missing Attachments or Tracking URLs</CardTitle>
        <div className="flex space-x-4 mt-4">
          <div className="flex flex-col">
            <span className="text-sm font-medium mb-2">Start Date</span>
            <DateInput
              value={startDate ? format(startDate, "yyyy-MM-dd") : ""}
              onChange={dateStr =>
                setStartDate(dateStr ? new Date(dateStr) : undefined)
              }
            />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-medium mb-2">End Date</span>
            <DateInput
              value={endDate ? format(endDate, "yyyy-MM-dd") : ""}
              onChange={dateStr =>
                setEndDate(dateStr ? new Date(dateStr) : undefined)
              }
            />
          </div>
          <div className="flex items-end">
            <Button onClick={fetchOrders} disabled={loading}>
              {loading ? "Loading..." : "Search"}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice Date</TableHead>
                <TableHead>Invoice Number</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Invoice Attachment</TableHead>
                <TableHead>Prescription Attachment</TableHead>
                <TableHead>Tracking URL</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayedOrders.length > 0 ? (
                displayedOrders.map(order => (
                  <TableRow key={order.invoice_number}>
                    <TableCell>{order.invoice_date}</TableCell>
                    <TableCell>
                      <a
                        href={`https://ops.pillplus.in/orders?orderId=${order.order_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:underline cursor-pointer"
                      >
                        {order.invoice_number}
                      </a>
                    </TableCell>
                    <TableCell>{order.status}</TableCell>
                    <TableCell>
                      <button
                        type="button"
                        className={cn(
                          "cursor-pointer bg-transparent border-none p-0 text-left",
                          order.invoice_attachment !== "Missing"
                            ? "text-blue-500 hover:underline"
                            : "text-red-500",
                        )}
                        onClick={() =>
                          handleLinkClick(order.invoice_attachment)
                        }
                        disabled={order.invoice_attachment === "Missing"}
                      >
                        {order.invoice_attachment !== "Missing"
                          ? "View Invoice"
                          : "Missing"}
                      </button>
                    </TableCell>
                    <TableCell>
                      <button
                        type="button"
                        className={cn(
                          "cursor-pointer bg-transparent border-none p-0 text-left",
                          order.prescription_attachment !== "Missing"
                            ? "text-blue-500 hover:underline"
                            : "text-red-500",
                        )}
                        onClick={() =>
                          handleLinkClick(order.prescription_attachment)
                        }
                        disabled={order.prescription_attachment === "Missing"}
                      >
                        {order.prescription_attachment !== "Missing"
                          ? "View Prescription"
                          : "Missing"}
                      </button>
                    </TableCell>
                    <TableCell>
                      <button
                        type="button"
                        className={cn(
                          "cursor-pointer bg-transparent border-none p-0 text-left",
                          order.tracking_url !== "Missing"
                            ? "text-blue-500 hover:underline"
                            : "text-red-500",
                        )}
                        onClick={() => handleLinkClick(order.tracking_url)}
                        disabled={order.tracking_url === "Missing"}
                      >
                        {order.tracking_url !== "Missing"
                          ? "Track Order"
                          : "Missing"}
                      </button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-6">
                    {loading
                      ? "Loading orders..."
                      : "No orders with missing attachments or tracking URLs found"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        {totalCount > 0 && (
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-gray-500">
              Showing {(currentPage - 1) * limit + 1} to{" "}
              {Math.min(currentPage * limit, totalCount)} of {totalCount}{" "}
              results
            </div>
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MissingAttachmentsTable;
